export const UNKNOWN_ACTOR_LABEL = 'Ukendt';

const ACTOR_BADGE_COLORS = [
    '#22d3ee',
    '#f59e0b',
    '#34d399',
    '#fb7185',
    '#a78bfa',
    '#f97316',
    '#84cc16',
    '#60a5fa',
];

export const getRecordActorId = (record) => {
    const actorId = typeof record?.user_id === 'string' ? record.user_id.trim() : '';
    return actorId || null;
};

export const getRecordCompanyId = (record) => {
    const companyId =
        (typeof record?.company_id === 'string' ? record.company_id : '') ||
        (typeof record?.company === 'string' ? record.company : '');
    const normalized = companyId.trim();
    return normalized || null;
};

export const getShortActorIdSuffix = (actorId) => {
    const normalized = String(actorId || '').trim();
    if (!normalized) return '????';
    return normalized.slice(-4).padStart(4, '?');
};

export const getUserDisplayName = (userInfo) => {
    if (!userInfo || typeof userInfo !== 'object') return '';
    return String(
        userInfo.name ||
        userInfo.display_name ||
        userInfo.email ||
        ''
    ).trim();
};

export const getInitialsFromDisplayName = (displayName) => {
    const normalized = String(displayName || '').trim();
    if (!normalized) return '';

    const tokens = normalized.split(/\s+/).filter(Boolean);
    if (tokens.length >= 2) {
        return `${tokens[0][0] || ''}${tokens[tokens.length - 1][0] || ''}`.toUpperCase();
    }

    return normalized.slice(0, 2).toUpperCase();
};

export const getDeterministicColorForActorId = (actorId) => {
    const normalized = String(actorId || '').trim();
    if (!normalized) return ACTOR_BADGE_COLORS[0];

    let hash = 0;
    for (let i = 0; i < normalized.length; i += 1) {
        hash = ((hash << 5) - hash) + normalized.charCodeAt(i);
        hash |= 0;
    }

    const index = Math.abs(hash) % ACTOR_BADGE_COLORS.length;
    return ACTOR_BADGE_COLORS[index];
};

export const buildCompanyMembershipViewAllByCompany = (userData) => {
    const memberships = userData?.company_memberships;
    if (!memberships || typeof memberships !== 'object' || Array.isArray(memberships)) {
        return {};
    }

    return Object.entries(memberships).reduce((acc, [companyId, permission]) => {
        const normalizedCompanyId = String(companyId || '').trim();
        if (!normalizedCompanyId) return acc;

        acc[normalizedCompanyId] = Boolean(
            permission?.can_view_all_company_data === true ||
            permission?.canViewAllCompanyData === true,
        );
        return acc;
    }, {});
};

export const buildAnonymizedActorLabelById = (records = []) => {
    const sortedRecords = [...records].sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
    const labels = {};
    let nextIndex = 1;

    sortedRecords.forEach((record) => {
        const actorId = getRecordActorId(record);
        if (!actorId || labels[actorId]) return;
        labels[actorId] = `Bruger ${nextIndex}`;
        nextIndex += 1;
    });

    return labels;
};

export const canSeeRealActorNameForRecord = ({ record, userData, companyMembershipViewAllByCompany }) => {
    if (userData?.isAdmin) return true;

    const companyId = getRecordCompanyId(record);
    if (!companyId) return false;

    return companyMembershipViewAllByCompany[companyId] === true;
};

export const getAppRecordActorDisplay = ({
    record,
    userData,
    companyMembershipViewAllByCompany,
    anonymizedActorLabelById,
    actorProfilesById,
}) => {
    const actorId = getRecordActorId(record);
    if (!actorId) return UNKNOWN_ACTOR_LABEL;

    if (!canSeeRealActorNameForRecord({ record, userData, companyMembershipViewAllByCompany })) {
        return anonymizedActorLabelById[actorId] || UNKNOWN_ACTOR_LABEL;
    }

    const profile = actorProfilesById[actorId];
    const displayName = getUserDisplayName(profile);
    if (displayName) return displayName;

    return `Bruger (mangler navn) ..${getShortActorIdSuffix(actorId)}`;
};

export const getAppRecordActorBadgeModel = ({
    record,
    userData,
    companyMembershipViewAllByCompany,
    anonymizedActorLabelById,
    actorProfilesById,
}) => {
    const actorId = getRecordActorId(record);
    if (!actorId) return null;

    const color = getDeterministicColorForActorId(actorId);
    const canSeeRealName = canSeeRealActorNameForRecord({ record, userData, companyMembershipViewAllByCompany });

    if (!canSeeRealName) {
        const anonymizedLabel = anonymizedActorLabelById[actorId] || UNKNOWN_ACTOR_LABEL;
        const anonymizedNumber = anonymizedLabel.match(/^(?:Bruger)\s+(\d+)$/i)?.[1] || '?';

        return {
            visible: true,
            text: `B${anonymizedNumber}`,
            tooltip: anonymizedLabel,
            color,
        };
    }

    const profile = actorProfilesById[actorId];
    const displayName = getUserDisplayName(profile);
    const initials = getInitialsFromDisplayName(displayName);
    const fallbackName = `Bruger (mangler navn) ..${getShortActorIdSuffix(actorId)}`;

    return {
        visible: true,
        text: initials || '??',
        tooltip: displayName || fallbackName,
        color,
    };
};
